import type {
  CarePlan,
  Patient,
  Practitioner,
  ServiceRequest,
} from "@curai/js-fhir-tools";
import { ChatActionType } from "../network/useChatAvailableActions";

export interface ChatUser {
  email: string;
  name: string;
  userId: number;
  professional?: {
    displayNameExternal: string;
    displayNameInternal: string;
    photo?: string;
    role: number;
    firstName?: string;
    lastName?: string;
    fhir: Practitioner;
  };
  patients?: Array<{
    patientId: string;
    primary: boolean;
    fhir: Patient;
  }>;
  [key: string]: unknown;
}

/**
 * A mapping of user IDs to ChatUser objects
 * Used throughout the chat feature to efficiently look up user information
 * for message attribution and display purposes
 */
export interface ChatUserMap {
  [userId: string]: ChatUser;
}

export interface ChatMessage {
  id?: string;
  message?: string;
  created: string;
  eocName?: string;
  type: MessageType | AsyncMessageTypes;
  uuid: string;
  userId?: number | null;
  roomToken?: string;
  fhirEncounterId?: string;
  modelType?: MessageModelType | string;
  encounterClassCode?: string;
  title?: string;
  text?: string;
  automation?: boolean;
  tag?: MessageTag | string;
  clientUuid?: string;
  final?: boolean;
  videoCallEvent?: string;
  practitionerName?: string | null;
  translationState?: TranslationState;
  translations?: {
    [lang: string]: string;
  };
  carePlan?: CarePlan;
  practitioner?: Practitioner;
  fhirPractitioner?: Practitioner;
  previousPractitioner?: Practitioner;
  questionnaireName?: string;
  roleHandedoffTo?: number;
  incomplete?: boolean;
  incompleteType?: string;
  documentReferenceId?: string;
  contentType?: string;
  filename?: string;
  originalUrl?: string;
  labRequests?: Array<
    Pick<
      ServiceRequest,
      | "id"
      | "status"
      | "intent"
      | "code"
      | "encounter"
      | "subject"
      | "requester"
      | "extension"
      | "meta"
      | "resourceType"
      | "authoredOn"
      | "category"
      | "identifier"
    >
  >;
}

export interface AmazonMessage extends ChatMessage {
  amazonId: string;
}

export enum TranslationState {
  COMPLETE = "COMPLETE",
  PENDING = "PENDING",
  ERROR = "ERROR",
}

/**
 * Message types enum for chat messages
 * Converted from Python constants
 */
export enum MessageType {
  // Basic message types
  TEXT = 2,
  PIN = 5,
  READ = 6,
  ACK = 7,
  UNACK = 79,
  START_TYPING = 8,
  STOP_TYPING = 9,
  PHOTO = 10,
  CONSULTATION_BUTTON = 11,
  BIO = 12,
  SERVICE_REQUEST = 13,
  REFRESH_SUBSCRIPTIONS = 14,
  AD = 15, // DEPRECATED - 12/23/16

  // SPADE TYPES
  ARTICLE = 16,
  SINGLE_SELECT = 17,
  MULTI_SELECT = 18,

  // CURAI DIAGNOSIS FLOW - DEPRECATED - 11/17/20
  CHIEF_COMPLAINT = 19,
  CHIEF_COMPLAINT_RESPONSE = 20,
  CONFIRM_CHIEF_COMPLAINT = 21,
  CONFIRM_CHIEF_COMPLAINT_RESPONSE = 22,
  NEXT_QUESTION = 23,
  NEXT_QUESTION_RESPONSE = 24,
  EXIT_NEXT_QUESTION = 26,

  // CURAI DIAGNOSIS FLOW - not deprecated
  WAIT_TIME = 25,

  // END CHAT - DEPRECATED - 11/17/20
  END_CHAT = 27,
  CONFIRM_END_CHAT = 28,

  // SUGGESTED RESPONSE - DEPRECATED - 11/17/20
  SUGGESTED_RESPONSE = 29,

  // INITIATE RATINGS - DEPRECATED - 11/17/20
  INITIATE_RATING = 30,
  CONFIRM_RATING = 31,

  // SYMPTOM EXTRACTOR - DEPRECATED - 11/17/20
  SYMPTOM_EXTRACTOR = 32,

  // USER INTENT - DEPRECATED - 11/17/20
  INTENT = 33,
  INTENT_RESPONSE = 34,

  // CONTENT SHARING - DEPRECATED - 11/17/20
  CONTENT_SHARE = 35,

  // INTAKE REVIEW
  WAIT_INTAKE_REVIEW = 36,

  // Content-only experience: initial card with recommendations.
  // DEPRECATED - 11/17/20
  CONTENT_WHILE_YOU_WAIT = 37,

  // END INTERACTION
  END_INTERACTION = 38,

  // Content-only experience: queries whether user still wants to chat.
  CONTENT_ONLY_CONFIRMATION = 39, // deprecated

  // General purpose "information" card with a message
  FIRST_OPINION_INFO = 40, // deprecated

  // Escalate to MD card
  ESCALATE_TO_MD = 41,

  // Elicit patient information
  PATIENT_INFO = 42,
  PATIENT_INFO_RESPONSE = 43,

  // Confirm prescriptions flow
  CONFIRM_RX = 44,
  CONFIRM_RX_RESPONSE = 45,

  // Doctor has entered to chat
  DOCTOR_HAS_ENTERED_TO_CHAT = 46,

  // Sent to frontends to force a refresh of LD feature flags / me / etc.
  FORCE_REFRESH = 47,

  // Encounter start and end
  ENCOUNTER_START = 48,
  ENCOUNTER_END = 49,

  // "Next Question" - Assisted Response Variant DEPRECATED 4/2023
  AR_QUESTION = 50,
  AR_QUESTION_RESPONSE = 51,

  // "Routing" message - tells the frontend to navigate to a route
  ROUTING = 52,

  // Confirm labs flow
  CONFIRM_LABS = 53,
  CONFIRM_LABS_RESPONSE = 54,

  // Care plan
  CARE_PLAN = 55,

  // Provider bio
  PROVIDER_BIO = 56,

  // Coronavirus test flow
  ROUTING_WITH_DETAILS = 57,

  PROVIDER_ALERT = 58,

  // patient id verification messages DEPRECATED 4/2021
  VERIFICATION_REQUEST = 59,
  VERIFICATION_RESPONSE = 60,
  VERIFICATION_CONFIRMATION = 61,

  // Enhanced History Gathering Questions DEPRECATED 4/2023
  PMH_QUESTION = 62,
  PMH_RESPONSE = 63,

  // Other encounter states
  ENCOUNTER_REOPEN = 64,
  ENCOUNTER_CLOSE = 65,
  INCOMPLETE_ON_CLOSE = 66,

  // Video call support
  VIDEO_CALL = 67,
  VIDEO_CALL_CANCELED = 71,

  // Provider only msgs
  HANDOFF = 68,
  NOTE_STARTED = 69,
  NOTE_CLOSED = 70,
  STEAL = 75,

  // Autochat
  AUTOSEND_INTRO = 72, // Deprecated 11/23
  SURVEY_MESSAGE = 73,

  INACTIVE_PATIENT = 74,

  // Indicates the message failed to transmit to a 3rd party. Similar to an
  // ACK message
  EXTERNAL_TRANSMISSION_FAILED = 76,

  // auto-closing autochats
  CLOSED_FOR_INACTIVITY = 77,

  // PDFs sent by patients still use the type: PHOTO
  // Unimplemented in chat stream, used exclusively to serve Clearstep PDFs
  EXTERNAL_PDF = 78,

  // Questionnaire messages
  QUESTIONNAIRE = 80,

  AUTOCHAT_FAILURE = 81,
}

export enum AsyncMessageTypes {
  TEXT = 1,
  VISIT_SUMMARY = 2,
  REOPEN = 3,
  LAB = 4,
  HEALTH_GOAL = 5,

  READ = 6,
  ACK = 7,
  PHOTO = 10,
  QUESTIONNAIRE = 80,

  CARE_PLAN = 8,
}

/**
 * Video call event types
 */
export const VIDEO_CALL_EVENT = {
  PROVIDER_CTA: "PROVIDER_CTA",
  PROVIDER_JOINED: "PROVIDER_JOINED",
  PATIENT_JOINED: "PATIENT_JOINED",
  PROVIDER_LEFT: "PROVIDER_LEFT",
};

export const VIDEO_CALL_STATUS = {
  MARKED_COMPLETE: "MARKED_COMPLETE",
  MARKED_INCOMPLETE: "MARKED_INCOMPLETE",
  CANCELED: "CANCELED",
};

/**
 * Enum for message tags used to identify the sender type
 */
export enum MessageTag {
  CUSTOMER = "CUSTOMER",
  PROFESSIONAL = "PROFESSIONAL",
  SYSTEM = "SYSTEM",
  AUTOMATED = "AUTOMATED",
  UNKNOWN = "UNKNOWN",
}

/**
 * Enum for message model types
 */
export enum MessageModelType {
  ASYNC_MESSAGE = "AsyncMessage",
  MESSAGE = "Message",
}

/**
 * Chat filter modes for message display and sending
 */
export enum ChatFilterMode {
  LIVE = "live",
  ASYNC = "async",
  COMBINED = "combined",
}

/**
 * Mapping from filter mode to chat action type for sending messages
 */
export const FILTER_TO_CHAT_ACTION: Record<ChatFilterMode, ChatActionType> = {
  [ChatFilterMode.LIVE]: ChatActionType.LIVE_CHAT,
  [ChatFilterMode.ASYNC]: ChatActionType.ASYNC_CHAT,
  [ChatFilterMode.COMBINED]: ChatActionType.LIVE_CHAT, // Default to live when combined
};
